import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, { signal });

    // Native click fallback: keep thumbnail navigation functional even if
    // the theme's declarative action handler or scroller is not ready.
    this.addEventListener('click', (event) => {
      const thumb = event.target?.closest?.('.slideshow-controls__thumbnail');
      if (!thumb || !this.contains(thumb)) return;
      const group = thumb.closest('slideshow-controls');
      const index = Array.from(group?.querySelectorAll('.slideshow-controls__thumbnail') || []).indexOf(thumb);
      if (index >= 0) this.#showSlide(index);
    }, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #showSlide(index) {
    const slideshow = this.slideshow;
    const slides = Array.from(slideshow?.querySelectorAll('slideshow-slides > slideshow-slide') || []);
    const slide = slides[index];
    if (!slide) return;
    slideshow?.select(index, undefined, { animate: false });
    const scroller = slideshow?.querySelector('slideshow-slides');
    if (scroller) {
      const horizontal = scroller.scrollWidth > scroller.clientWidth + 2;
      const vertical = scroller.scrollHeight > scroller.clientHeight + 2;
      if (horizontal || vertical) {
        scroller.scrollTo({
          left: horizontal ? slide.offsetLeft - scroller.offsetLeft : scroller.scrollLeft,
          top: !horizontal && vertical ? slide.offsetTop - scroller.offsetTop : scroller.scrollTop,
          behavior: 'instant'
        });
      }
    }
    this.querySelectorAll('slideshow-controls').forEach((controls) => {
      controls.querySelectorAll('.slideshow-controls__thumbnail').forEach((el, i) => {
        el.setAttribute('aria-selected', String(i === index));
      });
    });
  }

  #handleVariantUpdate = (event) => {
    const source = event.detail?.data?.html;
    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    const featuredMediaId = event.detail.resource?.featured_media?.id;
    this.replaceWith(newMediaGallery);
    if (featuredMediaId) {
      requestAnimationFrame(() => {
        const slides = Array.from(newMediaGallery.querySelectorAll('slideshow-slides > slideshow-slide'));
        const index = slides.findIndex((slide) =>
          slide.getAttribute('slide-id') === String(featuredMediaId) ||
          Boolean(slide.querySelector('[data-media-id="' + String(featuredMediaId) + '"]'))
        );
        if (index >= 0) newMediaGallery.#showSlide(index);
      });
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
